import React, { useRef } from "react";
import { graphql } from "gatsby";
import { Box } from "@indeed/ifl-components";
import { Grid, GridItem } from "components/grid";
import ArticleHero from "components/article/articleHero";
import ArticleFooter from "components/article/articleFooter";
import { ArticlesSecondary } from "components/articlesSecondary";
import { Pagination } from "components/pagination";
import { useContainerSize } from "hooks/useContainerSize";
import { articleContentStyles, heroStyles } from "styles/common";
import {
  formatArticle,
  formatArticles,
  formatArticleSeo,
} from "helpers/formatData"; // maybe todo?
import { contentParser } from "helpers/contentParser";
import { SEO } from "components/seo";

const ArticlePage = ({ data: { article, articles } }) => {
  const contentWidth = 800;
  const articleRef = useRef();
  const { width } = useContainerSize(articleRef);
  const articleIndex = articles.nodes
    .map((node) => node.id)
    .indexOf(article.id);
  const before = articles.nodes[articleIndex + 1];
  const after = articles.nodes[articleIndex - 1];

  const recommendedArticles = formatArticles([
    before ? before : articles.nodes[0],
    articles.nodes[articleIndex + 2]
      ? articles.nodes[articleIndex + 2]
      : articles.nodes[1],
    articles.nodes[articleIndex + 3]
      ? articles.nodes[articleIndex + 3]
      : articles.nodes[2],
  ]);

  const pageOrder = {
    next: { to: after ? `/article/${after.slug}` : "/articles" },
    nextLabel: after ? "Next" : "All articles",
    previous: { to: before ? `/article/${before.slug}` : "/articles" },
    previousLabel: before ? "Previous" : "All articles",
  };

  const {
    altText,
    animationUrl,
    articleContent,
    authorAvatar,
    authorAvatarAltText,
    authorLink,
    author,
    authorTitle,
    authorPlaceOfWork,
    categories,
    contributorList,
    illustrator,
    illustratorAvatar,
    illustratorAvatarAltText,
    illustratorLink,
    illustratorPlaceOfWork,
    illustratorTitle,
    image,
    gatsbyImageData,
    mediaType,
    publishDate,
  } = formatArticle(article);

  const articleUrl = `https://indeed.design/article/${article.slug}`;

  const content = contentParser(articleContent, {
    maxWidth: width,
    contentWidth,
  });

  return (
    <>
      <Box
        id="article-container"
        ref={articleRef}
        as="article"
        sx={{
          ...heroStyles,
        }}
      >
        <ArticleHero
          title={article.title}
          subTitle={article.content}
          contributorList={contributorList}
          article={{
            altText,
            animationUrl,
            articleUrl,
            authorAvatar,
            authorAvatarAltText,
            authorLink,
            author,
            authorTitle,
            articleContent,
            categories,
            illustrator,
            illustratorLink,
            image,
            gatsbyImageData,
            mediaType,
            publishDate,
          }}
        />
        <Grid templateColumns={"repeat(8, 1fr)"}>
          <GridItem
            colSpan={8}
            colStart={1}
            sx={{ maxWidth: ["100%", null, null, contentWidth], mx: "auto" }}
          >
            {articleContent ? (
              <Box
                as="section"
                id="article-content"
                aria-label="Article Content"
                sx={{
                  fontFamily: "Indeed Sans",
                  fontWeight: "normal",
                  lineHeight: "normal",
                  fontSize: [3, null, 4, 4],
                  color: "neutral.800",
                  maxWidth: ["100%", null, null, contentWidth],
                  img: {
                    display: "block",
                    m: "auto",
                  },
                  ...articleContentStyles,
                }}
              >
                {content}
              </Box>
            ) : null}
          </GridItem>
          <GridItem
            colSpan={8}
            colStart={1}
            sx={{
              maxWidth: ["100%", null, contentWidth],
              mx: [0, null, null, "auto"],
              width: ["auto", null, "100%"],
            }}
          >
            <ArticleFooter
              articleTitle={article.title}
              articleUrl={articleUrl}
              authorName={author.name}
              contributorList={contributorList}
            />
          </GridItem>
          <GridItem colSpan={8}>
            <Pagination {...pageOrder} sx={{ p: "40px 0" }} />
          </GridItem>
          <GridItem colSpan={8}>
            <Box as="hr" sx={{ my: 6 }} />
            <ArticlesSecondary
              heading="Featured articles"
              articles={recommendedArticles}
            />
          </GridItem>
        </Grid>
      </Box>
    </>
  );
};

export default ArticlePage;

export const Head = ({ data: { article } }) => {
  const { author, fixedImage } = formatArticle(article);
  const thumbnail = `https://indeed.design/${fixedImage.src}`;
  const description = article?.content
    ?.replace("<p>", "")
    .replace("</p>", "")
    .trim();

  const articleSeo = formatArticleSeo(
    article.seo,
    article.title,
    description,
    thumbnail
  );

  return (
    <>
      <SEO {...articleSeo} />
      <meta property="og:site_name" content="Indeed Design" />
      <meta name="article:author" content={author} />
      <meta name="twitter:title" content={article.title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={thumbnail} />
      <meta name="twitter:image:src" content={thumbnail} />
      <meta name="twitter:card" content="summary_large_image" />
      <script
        async
        defer
        crossOrigin="anonymous"
        src="https://platform.twitter.com/widgets.js"
      />
      <script
        async
        defer
        crossOrigin="anonymous"
        src="https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v11.0"
        nonce="0cuvLEoH"
      />
    </>
  );
};

export const query = graphql`
  query article($id: StringQueryOperatorInput) {
    article: wpPost(id: $id) {
      id
      title
      content
      slug
      categories {
        nodes {
          name
          slug
        }
      }
      post_fields_article {
        externalLink
        mediaType: articleMediaType
        animationUrl: articleAnimationUrl {
          mediaItemUrl
        }
        image {
          sourceUrl
          altText
          localFile {
            childImageSharp {
              gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
              fixed(cropFocus: CENTER, width: 1200, height: 630) {
                base64
                width
                height
                src
                srcSet
              }
            }
          }
        }
        authorName
        authorTitle
        authorLink
        authorPlaceOfWork
        authorAvatar {
          sourceUrl
          altText
        }
        illustratorName
        illustratorTitle
        illustratorLink
        illustratorPlaceOfWork
        illustratorAvatar {
          sourceUrl
          altText
        }
        contributorGroup {
          contributorType
          contributorLink
          contributorName
          contributorAvatar {
            sourceUrl
            altText
          }
          contributorTitle
          contributorPlaceOfWork
        }
        articleContent
        articlePublishDate
      }
      seo {
        seoTitle
        seoDescription
        seoOgTitle
        seoOgDescription
        seoCanonicalUrl
        seoCustomFields
        seoImage {
          sourceUrl
        }
      }
    }
    articles: allWpPost(
      filter: { status: { eq: "publish" } }
      sort: { date: DESC }
    ) {
      nodes {
        id
        title
        content
        slug
        categories {
          nodes {
            name
            slug
          }
        }
        post_fields_article {
          externalLink
          image {
            sourceUrl
            altText
            localFile {
              childImageSharp {
                gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
              }
            }
          }
          authorName
          authorTitle
          authorLink
          authorPlaceOfWork
          authorAvatar {
            sourceUrl
            altText
          }
          articleContent
          articlePublishDate
        }
      }
    }
  }
`;
